import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { JourneyApi } from "@/api/client";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScreenFrame } from "@/components/ScreenFrame";
import { ExperienceV2 } from "@/experience-v2/ExperienceV2";
import { DEFAULT_DEMO_QUESTION } from "@/lib/constants";
import { createTicketCode, TICKET_TEMPLATE_NO, todayLabel } from "@/lib/ids";
import { actionKeywordFromText, cleanUserText } from "@/lib/text";
import { downloadTicketPng } from "@/lib/ticket";
import { SoundManager } from "@/lib/sound";
import type { JourneyState, Stage } from "@/types/journey";
import {
  AboutPage,
  ActionInputPage,
  ActionRefinePage,
  AnalysisLoadingPage,
  BinaryQuestionPage,
  CasePage,
  EndPage,
  IntroPage,
  KnowledgePage,
  LightsPage,
  LoadingScreen,
  QuestionInputPage,
  RecapPage,
  ReflectionPage,
  SharePage,
  ThirdQuestionPage,
  TicketPage,
} from "@/pages/JourneyPages";

const initialState: JourneyState = {
  sessionId: "",
  stage: "LOADING",
  question: "",
  analysis: null,
  questionTwo: null,
  firstAnswer: "",
  secondAnswer: "",
  thirdAnswer: "",
  reflection: null,
  knowledge: [],
  jiaxingCase: null,
  actionOriginal: "",
  actionRefined: "",
  finalAction: "",
  ticket: null,
  stats: 0,
  aiSource: "mock",
};

const progressByStage: Record<Stage, 0 | 1 | 2 | 3 | 4> = {
  LOADING: 0,
  INTRO: 0,
  QUESTION_INPUT: 0,
  AI_ANALYSIS: 0,
  QUESTION_ONE: 0,
  QUESTION_TWO: 1,
  QUESTION_THREE: 2,
  JOURNEY_RECAP: 3,
  PERSONAL_REFLECTION: 3,
  KNOWLEDGE: 3,
  JIAXING_CASE: 3,
  ACTION_INPUT: 3,
  ACTION_REFINE: 3,
  TICKET: 4,
  LIGHTS: 4,
  END: 4,
  ABOUT: 4,
  SHARE: 0,
};

function sourceLabel(source: "api" | "deepseek" | "local-mock" | "local-fallback"): "openai" | "deepseek" | "mock" | "fallback" {
  if (source === "deepseek") return "deepseek";
  if (source === "api") return "openai";
  if (source === "local-mock") return "mock";
  return "fallback";
}

function AppContent() {
  const api = useMemo(() => new JourneyApi(), []);
  const sound = useMemo(() => new SoundManager(), []);
  const isDemo = window.location.search.includes("demo=1");
  const isShare = window.location.pathname === "/share";
  const [journey, setJourney] = useState<JourneyState>({ ...initialState, question: isDemo ? DEFAULT_DEMO_QUESTION : "" });
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [firstChoice, setFirstChoice] = useState<"A" | "B" | null>(null);
  const [secondChoice, setSecondChoice] = useState<"A" | "B" | null>(null);
  const [refineChoice, setRefineChoice] = useState<"refined" | "original">("refined");
  const [busy, setBusy] = useState(false);
  const [contentBusy, setContentBusy] = useState(false);
  const [ticketBusy, setTicketBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const reflectionRequested = useRef(false);
  const knowledgeRequested = useRef(false);
  const caseRequested = useRef(false);

  useEffect(() => {
    let active = true;
    void api.startSession().then((sessionId) => {
      if (active) setJourney((current) => ({ ...current, sessionId }));
    });
    const timer = window.setTimeout(() => {
      if (active) setJourney((current) => ({ ...current, stage: isShare ? "SHARE" : "INTRO" }));
    }, 1850);
    return () => {
      active = false;
      window.clearTimeout(timer);
      sound.dispose();
    };
  }, [api, isShare, sound]);

  useEffect(() => {
    if (journey.stage !== "PERSONAL_REFLECTION" || journey.reflection || reflectionRequested.current) return;
    reflectionRequested.current = true;
    setBusy(true);
    void api.reflection({
      sessionId: journey.sessionId,
      originalQuestion: journey.question,
      firstAnswer: journey.firstAnswer,
      secondAnswer: journey.secondAnswer,
      thirdAnswer: journey.thirdAnswer,
    }).then((result) => {
      setJourney((current) => ({ ...current, reflection: result.value, aiSource: sourceLabel(result.source) }));
      if (result.source !== "api") showNotice("南湖起了一阵风，我们换一种方式继续。", setNotice);
    }).finally(() => setBusy(false));
  }, [api, journey.firstAnswer, journey.question, journey.reflection, journey.secondAnswer, journey.sessionId, journey.stage, journey.thirdAnswer]);

  useEffect(() => {
    if (journey.stage !== "KNOWLEDGE" || knowledgeRequested.current) return;
    knowledgeRequested.current = true;
    setContentBusy(true);
    void api.publishedKnowledge().then((knowledge) => {
      setJourney((current) => ({ ...current, knowledge }));
    }).finally(() => setContentBusy(false));
  }, [api, journey.stage]);

  useEffect(() => {
    if (journey.stage !== "JIAXING_CASE" || caseRequested.current) return;
    caseRequested.current = true;
    setContentBusy(true);
    void api.publishedCase(journey.analysis?.topic ?? "innovation").then((jiaxingCase) => {
      setJourney((current) => ({ ...current, jiaxingCase }));
    }).finally(() => setContentBusy(false));
  }, [api, journey.analysis?.topic, journey.stage]);

  function showNotice(message: string, setter = setNotice): void {
    setter(message);
    window.setTimeout(() => setter(null), 4200);
  }

  function update(patch: Partial<JourneyState>): void {
    setJourney((current) => ({ ...current, ...patch }));
  }

  function handleSoundChange(enabled: boolean): void {
    setSoundEnabled(enabled);
    sound.setEnabled(enabled);
    if (enabled) sound.blip("water");
  }

  function handleRestart(): void {
    reflectionRequested.current = false;
    knowledgeRequested.current = false;
    caseRequested.current = false;
    setFirstChoice(null);
    setSecondChoice(null);
    setRefineChoice("refined");
    setBusy(false);
    setJourney({ ...initialState, stage: "INTRO", question: isDemo ? DEFAULT_DEMO_QUESTION : "", sessionId: journey.sessionId });
  }

  function handleStart(): void {
    update({ stage: "QUESTION_INPUT", question: isDemo && !journey.question ? DEFAULT_DEMO_QUESTION : journey.question });
  }

  async function handleQuestionSubmit(): Promise<void> {
    const safeQuestion = cleanUserText(journey.question);
    if (safeQuestion.length < 5 || safeQuestion.length > 20) {
      showNotice("先把问题写在 5 到 20 个字之间。", setNotice);
      return;
    }
    update({ stage: "AI_ANALYSIS", question: safeQuestion });
    setBusy(true);
    const result = await api.analyzeQuestion(journey.sessionId, safeQuestion);
    update({ stage: "QUESTION_ONE", analysis: result.value, aiSource: sourceLabel(result.source) });
    setBusy(false);
    if (result.source !== "api") showNotice("南湖起了一阵风，我们换一种方式继续。", setNotice);
  }

  async function handleFirstChoice(value: "A" | "B"): Promise<void> {
    if (!journey.analysis || firstChoice) return;
    setFirstChoice(value);
    sound.blip("choice");
    const answer = value === "A" ? journey.analysis.questionOne.optionA : journey.analysis.questionOne.optionB;
    update({ firstAnswer: answer });
    setBusy(true);
    const result = await api.nextQuestion(journey.sessionId, journey.analysis.topic, value);
    update({ questionTwo: result.value.questionTwo, aiSource: sourceLabel(result.source) });
    if (result.source !== "api") showNotice("南湖起了一阵风，我们换一种方式继续。", setNotice);
    window.setTimeout(() => update({ stage: "QUESTION_TWO" } as Partial<JourneyState>), 560);
    setBusy(false);
  }

  function handleSecondChoice(value: "A" | "B"): void {
    if (!journey.questionTwo || secondChoice) return;
    setSecondChoice(value);
    sound.blip("choice");
    const answer = value === "A" ? journey.questionTwo.optionA : journey.questionTwo.optionB;
    update({ secondAnswer: answer });
    setBusy(true);
    window.setTimeout(() => {
      setBusy(false);
      update({ stage: "QUESTION_THREE" } as Partial<JourneyState>);
    }, 560);
  }

  function handleThirdSubmit(value?: string): void {
    const answer = cleanUserText(value ?? journey.thirdAnswer);
    if (!answer) {
      showNotice("留下一个真实的回答，哪怕只是“我会犹豫”。", setNotice);
      return;
    }
    update({ thirdAnswer: answer, stage: "JOURNEY_RECAP" });
    sound.blip("water");
  }

  function handleReflectionContinue(): void {
    setJourney((current) => ({ ...current, stage: "KNOWLEDGE" }));
  }

  function handleKnowledgeContinue(): void {
    setJourney((current) => ({ ...current, stage: "JIAXING_CASE" }));
  }

  function handleCaseContinue(): void {
    setJourney((current) => ({ ...current, stage: "ACTION_INPUT" }));
  }

  async function handleActionSubmit(): Promise<void> {
    const action = cleanUserText(journey.actionOriginal);
    if (!action) {
      showNotice("写下一件明天能做到的小事吧。", setNotice);
      return;
    }
    setBusy(true);
    const result = await api.refineAction(journey.sessionId, action);
    update({ actionOriginal: result.value.original, actionRefined: result.value.refined, stage: "ACTION_REFINE", aiSource: sourceLabel(result.source) });
    setBusy(false);
    if (result.source !== "api") showNotice("南湖起了一阵风，我们换一种方式继续。", setNotice);
  }

  function handleRefineContinue(): void {
    const finalAction = refineChoice === "refined" ? journey.actionRefined : journey.actionOriginal;
    update({ finalAction, ticket: { action: finalAction, templateNo: TICKET_TEMPLATE_NO, issuedDate: todayLabel(), ticketCode: createTicketCode() }, stage: "TICKET" });
  }

  async function handleTicketGenerate(): Promise<void> {
    if (!journey.ticket || ticketBusy) return;
    setTicketBusy(true);
    const keyword = actionKeywordFromText(journey.finalAction);
    try {
      const result = await api.complete({
        sessionId: journey.sessionId,
        topic: journey.analysis?.topic ?? "other",
        actionKeyword: keyword,
        caseId: journey.jiaxingCase?.id ?? null,
      });
      update({ stats: result.value.completed });
      sound.blip("stamp");
      await downloadTicketPng(journey.ticket);
      void api.track(journey.sessionId, "ticket_generated");
    } catch {
      showNotice("船票已生成，但浏览器没有完成图片保存；你仍然可以继续。", setNotice);
    }
    window.setTimeout(() => {
      setTicketBusy(false);
      update({ stage: "LIGHTS" });
    }, 1200);
  }

  function renderStage(): ReactNode {
    switch (journey.stage) {
      case "INTRO": return <IntroPage isDemo={isDemo} onStart={handleStart} />;
      case "QUESTION_INPUT": return <QuestionInputPage question={journey.question} isDemo={isDemo} onChange={(question) => update({ question })} onSubmit={() => void handleQuestionSubmit()} busy={busy} />;
      case "AI_ANALYSIS": return <AnalysisLoadingPage />;
      case "QUESTION_ONE": return journey.analysis ? <BinaryQuestionPage index={1} question={journey.analysis.questionOne} selected={firstChoice} pending={busy} onSelect={(value) => void handleFirstChoice(value)} /> : <AnalysisLoadingPage />;
      case "QUESTION_TWO": return journey.questionTwo ? <BinaryQuestionPage index={2} question={journey.questionTwo} selected={secondChoice} pending={busy} onSelect={handleSecondChoice} /> : <AnalysisLoadingPage />;
      case "QUESTION_THREE": return <ThirdQuestionPage value={journey.thirdAnswer} onChange={(thirdAnswer) => update({ thirdAnswer })} onSubmit={handleThirdSubmit} />;
      case "JOURNEY_RECAP": return <RecapPage question={journey.question} firstAnswer={journey.firstAnswer} secondAnswer={journey.secondAnswer} thirdAnswer={journey.thirdAnswer} onContinue={() => update({ stage: "PERSONAL_REFLECTION" })} />;
      case "PERSONAL_REFLECTION": return <ReflectionPage question={journey.question} reflection={journey.reflection} source={journey.aiSource} loading={busy} onContinue={handleReflectionContinue} />;
      case "KNOWLEDGE": return <KnowledgePage cards={journey.knowledge} onContinue={handleKnowledgeContinue} />;
      case "JIAXING_CASE": return <CasePage item={journey.jiaxingCase} onContinue={handleCaseContinue} />;
      case "ACTION_INPUT": return <ActionInputPage value={journey.actionOriginal} onChange={(actionOriginal) => update({ actionOriginal })} onSubmit={() => void handleActionSubmit()} />;
      case "ACTION_REFINE": return <ActionRefinePage result={{ original: journey.actionOriginal, refined: journey.actionRefined }} choice={refineChoice} onChoice={setRefineChoice} onContinue={handleRefineContinue} />;
      case "TICKET": return journey.ticket ? <TicketPage ticket={journey.ticket} onGenerate={() => void handleTicketGenerate()} busy={ticketBusy} /> : null;
      case "LIGHTS": return <LightsPage count={journey.stats} action={actionKeywordFromText(journey.finalAction)} onContinue={() => update({ stage: "END" })} />;
      case "END": return <EndPage onRestart={handleRestart} onTicket={() => update({ stage: "TICKET" })} onAbout={() => update({ stage: "ABOUT" })} />;
      case "ABOUT": return <AboutPage onBack={() => update({ stage: "END" })} />;
      case "SHARE": return <SharePage onStart={() => update({ stage: "INTRO" })} />;
      default: return <IntroPage isDemo={isDemo} onStart={handleStart} />;
    }
  }

  if (journey.stage === "LOADING") return <LoadingScreen />;

  return (
    <ScreenFrame stage={journey.stage} progress={progressByStage[journey.stage]} soundEnabled={soundEnabled} onSoundChange={handleSoundChange} onRestart={handleRestart}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={journey.stage} className="stage-transition" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}>
          {renderStage()}
        </motion.div>
      </AnimatePresence>
      {contentBusy ? <div className="content-loading" role="status">正在从审核内容库读取……</div> : null}
      {notice ? <div className="notice" role="status"><span>⌁</span>{notice}</div> : null}
    </ScreenFrame>
  );
}

export default function App() {
  const useLegacyExperience = new URLSearchParams(window.location.search).get("v1") === "1" || window.location.pathname === "/share";
  if (useLegacyExperience) return <ErrorBoundary><AppContent /></ErrorBoundary>;
  return <ErrorBoundary><ExperienceV2 /></ErrorBoundary>;
}
